import { useEffect, useLayoutEffect, useRef, useState } from "react";
import i18n from "../../../language/i18n";
import { Icon } from "../../../library";
import { useTour } from "../tour/tourContext";
import { INTRO_TOUR_SEEN_KEY } from "../tour/TourProvider";
import { taskProgressIndicatorStyles } from "./TaskProgressIndicatorStyles";

interface TaskProgressIndicatorProps {
  loading?: boolean;
  taskItemProgressDTOList?: any[];
  language?: string;
}

// localStorage keys (app-namespaced): pill position, hidden state, tour-seen, weekly peek
const LS_POS = "cu_tasks_pos";
const LS_MIN = "cu_tasks_minimized";
const LS_TOUR = "cu_tasks_tour_done";
const LS_PEEK = "cu_tasks_peek_week";

// ── Design-system task glyphs (inline SVG, ported 1:1 from Playful Frost) ──
const TaskGlyph = ({ name }: { name: string }) => {
  const p = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  if (name === "clock")
    return (
      <svg {...(p as any)}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.5V12l3 1.8" />
      </svg>
    );
  if (name === "refresh")
    return (
      <svg {...(p as any)}>
        <path d="M4.5 11a7.5 7.5 0 0 1 12.8-4.2L20 9" />
        <path d="M19.5 13a7.5 7.5 0 0 1-12.8 4.2L4 15" />
        <path d="M20 4.5V9h-4.5M4 19.5V15h4.5" />
      </svg>
    );
  if (name === "sparkles")
    return (
      <svg {...(p as any)} fill="currentColor" stroke="none">
        <path d="M12 3l1.7 5.1L19 10l-5.3 1.9L12 17l-1.7-5.1L5 10l5.3-1.9z" />
        <path d="M18.4 14.2l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" />
      </svg>
    );
  if (name === "check")
    return (
      <svg {...(p as any)}>
        <path d="M5 12.5l4.5 4.5L19 7" />
      </svg>
    );
  if (name === "pencil")
    return (
      <svg {...(p as any)}>
        <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17z" />
        <path d="M13.5 6.5l3 3" />
      </svg>
    );
  if (name === "chat")
    return (
      <svg {...(p as any)}>
        <path d="M4 5.5h16v9H10l-4 3.5v-3.5H4z" />
        <path d="M8 10h.01M12 10h.01M16 10h.01" />
      </svg>
    );
  if (name === "book")
    return (
      <svg {...(p as any)}>
        <path d="M12 6.5C10.5 5.4 8.5 5 6 5v12c2.5 0 4.5.4 6 1.5" />
        <path d="M12 6.5C13.5 5.4 15.5 5 18 5v12c-2.5 0-4.5.4-6 1.5z" />
      </svg>
    );
  if (name === "trophy")
    return (
      <svg {...(p as any)}>
        <path d="M8 4.5h8v3.5a4 4 0 0 1-8 0z" />
        <path d="M8 5.5H5.5V7A2.5 2.5 0 0 0 8 9.5M16 5.5h2.5V7A2.5 2.5 0 0 1 16 9.5" />
        <path d="M12 11.5V15M9.5 19h5M10 19c0-1.4.6-2.3 2-2.3s2 .9 2 2.3" />
      </svg>
    );
  if (name === "coin")
    return (
      <svg {...(p as any)}>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="4" />
      </svg>
    );
  if (name === "bolt")
    return (
      <svg {...(p as any)}>
        <path d="M13 3 5 13h6l-1 8 8-10h-6z" />
      </svg>
    );
  if (name === "clipboard")
    return (
      <svg {...(p as any)}>
        <rect x="6" y="5" width="12" height="15" rx="2" />
        <path d="M9 5V4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V5" />
        <path d="M9 11h6M9 15h4" />
      </svg>
    );
  return null;
};

// sourceType → glyph + tile palette (pastel tint background / ink icon color)
const SOURCE_STYLE: Record<string, { icon: string; tint: string; ink: string }> = {
  ACTIVITY: { icon: "pencil", tint: "#e6f7f6", ink: "#1F8A82" },
  ACTIVITY_CORRECT: { icon: "sparkles", tint: "#e8fff3", ink: "#2E7D55" },
  ACTIVITY_REVIEW: { icon: "refresh", tint: "#e7f0ff", ink: "#3F77C2" },
  ACTIVITY_TIME_SPENT: { icon: "clock", tint: "#f1e6ff", ink: "#7C4DD6" },
  ACTIVITY_TEMPLATE_CHAT: { icon: "chat", tint: "#fff3e0", ink: "#C9772B" },
  SUBJECT_DESCRIPTION_CHAT: { icon: "book", tint: "#eaeaff", ink: "#5A53C9" },
  DUEL: { icon: "trophy", tint: "#fff7da", ink: "#B8860B" },
  PERSONALIZED_ASSIGNMENT: { icon: "clipboard", tint: "#ffe9ef", ink: "#C2466B" },
  LOGIN: { icon: "bolt", tint: "#e4f8fb", ink: "#2196A6" },
  EARNED_POINTS: { icon: "coin", tint: "#fdf2cf", ink: "#B8860B" },
  BRANCH_ASSIGNMENT: { icon: "sparkles", tint: "#e8fff3", ink: "#2E7D55" },
};
const sourceStyleOf = (s: string) =>
  SOURCE_STYLE[s] || { icon: "sparkles", tint: "#e7f0ff", ink: "#3F77C2" };

// ── First-run 3-step tour coachmark (ported 1:1) ──
const TaskTour = ({ pos, onEnd }: { pos: any; onEnd: () => void }) => {
  const [step, setStep] = useState(0);
  const tourRef = useRef<HTMLDivElement>(null);
  // Measured placement: tour top + the arrow's offset so it keeps pointing at
  // the pill. Null until the first layout pass measures the real height.
  const [box, setBox] = useState<{ top: number; arrow: number } | null>(null);
  const STEPS = [
    { icon: "sparkles", title: i18n.t("tasks_weekly"), body: i18n.t("tasks_tour_1") },
    { icon: "refresh", title: i18n.t("tasks_tour_2_title"), body: i18n.t("tasks_tour_2") },
    { icon: "clock", title: i18n.t("tasks_tour_3_title"), body: i18n.t("tasks_tour_3") },
  ];
  const s = STEPS[step];
  const last = step === STEPS.length - 1;
  const p = pos || {
    x: 88,
    y: (typeof window !== "undefined" ? window.innerHeight : 700) - 70,
  };
  // Center the coachmark on the pill, then clamp it fully inside the viewport
  // so a bottom-anchored pill can't push the lower half off-screen. The body
  // text length varies per step, so we measure the rendered height each step.
  useLayoutEffect(() => {
    const el = tourRef.current;
    if (!el) return;
    const M = 12,
      PILL = 66;
    const vh = window.innerHeight;
    const h = el.offsetHeight;
    const pillCenter = p.y + PILL / 2;
    let top = pillCenter - h / 2;
    top = Math.max(M, Math.min(vh - h - M, top));
    // Keep the arrow aimed at the pill even after the panel is clamped.
    const arrow = Math.max(14, Math.min(h - 14, pillCenter - top));
    setBox({ top, arrow });
  }, [step, p.x, p.y]);
  const style = box
    ? { left: p.x + 78 + "px", top: box.top + "px" }
    : {
        left: p.x + 78 + "px",
        top: Math.max(12, p.y - 70) + "px",
        visibility: "hidden" as const,
      };
  return (
    <div
      ref={tourRef}
      className="tk-tour"
      style={style}
      role="dialog"
      aria-label={i18n.t("tasks_weekly")}
    >
      <span
        className="tk-tour__arrow"
        style={box ? { top: box.arrow + "px" } : undefined}
      />
      <div className="tk-tour__head">
        <span className="tk-tour__ico">
          <TaskGlyph name={s.icon} />
        </span>
        <span className="tk-tour__step">
          {step + 1} / {STEPS.length}
        </span>
      </div>
      <div className="tk-tour__title">{s.title}</div>
      <div className="tk-tour__body">{s.body}</div>
      <div className="tk-tour__dots">
        {STEPS.map((_, i) => (
          <span
            key={i}
            className={"tk-tour__dot" + (i === step ? " is-on" : "")}
          />
        ))}
      </div>
      <div className="tk-tour__actions">
        <button className="tk-tour__skip" onClick={onEnd}>
          {i18n.t("tasks_skip")}
        </button>
        <button
          className="tk-tour__next"
          onClick={() => (last ? onEnd() : setStep((v) => v + 1))}
        >
          {last ? i18n.t("tasks_got_it") : i18n.t("tasks_next")}
        </button>
      </div>
    </div>
  );
};

const TaskProgressIndicator = ({
  loading,
  taskItemProgressDTOList,
  language,
}: TaskProgressIndicatorProps) => {
  const dto =
    taskItemProgressDTOList &&
    taskItemProgressDTOList.length > 0
      ? taskItemProgressDTOList[0]
      : null;
  const itemDTOs =
    dto && dto.taskItemDTOList ? dto.taskItemDTOList : [];
  const hasData =
    !loading &&
    !!dto &&
    itemDTOs.length > 0 &&
    itemDTOs.every((d: any) => d.taskItemProgressDTO);

  // ── UI state (all hooks run unconditionally, before any early return) ──
  const [open, setOpen] = useState(false);
  const [peek, setPeek] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(LS_MIN) === "1";
    } catch (e) {
      return false;
    }
  });
  const minimize = () => {
    setOpen(false);
    setDismissed(true);
    try {
      localStorage.setItem(LS_MIN, "1");
    } catch (e) {}
  };
  const restore = () => {
    setDismissed(false);
    try {
      localStorage.setItem(LS_MIN, "0");
    } catch (e) {}
  };
  const [pos, setPos] = useState<any>(() => {
    try {
      const s = localStorage.getItem(LS_POS);
      if (s) return JSON.parse(s);
    } catch (e) {}
    return null; // null = default CSS anchor (sidebar bottom-left)
  });
  const drag = useRef<any>(null);
  const [dragging, setDragging] = useState(false);
  const [tour, setTour] = useState(() => {
    try {
      return localStorage.getItem(LS_TOUR) !== "1";
    } catch (e) {
      return true;
    }
  });
  // Never stack the tasks coachmark on top of the guided tour: wait until the
  // first-run intro tour has been seen and no tour is currently running.
  const { isActive: guidedTourActive } = useTour();
  const introTourSeen = (() => {
    try {
      return localStorage.getItem(INTRO_TOUR_SEEN_KEY) === "true";
    } catch (e) {
      return true;
    }
  })();
  const showTour = tour && introTourSeen && !guidedTourActive;
  const endTour = () => {
    setTour(false);
    try {
      localStorage.setItem(LS_TOUR, "1");
    } catch (e) {}
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest(".tk-pill-dismiss")) return;
    const startX = e.clientX,
      startY = e.clientY;
    const base = pos || { x: 88, y: window.innerHeight - 70 };
    drag.current = {
      startX,
      startY,
      baseX: base.x,
      baseY: base.y,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.startX,
      dy = e.clientY - drag.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      drag.current.moved = true;
      setDragging(true);
    }
    const nx = Math.max(
      6,
      Math.min(window.innerWidth - 70, drag.current.baseX + dx),
    );
    const ny = Math.max(
      6,
      Math.min(window.innerHeight - 70, drag.current.baseY + dy),
    );
    setPos({ x: nx, y: ny });
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    const moved = drag.current.moved;
    drag.current = null;
    setDragging(false);
    if (pos) {
      try {
        localStorage.setItem(LS_POS, JSON.stringify(pos));
      } catch (er) {}
    }
    if (!moved) setOpen((v) => !v);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Peek the label once per week (first visit of the ISO week)
  useEffect(() => {
    if (!hasData) return;
    const wk = (() => {
      const d = new Date();
      const o = new Date(d.getFullYear(), 0, 1);
      return (
        d.getFullYear() +
        "-" +
        Math.ceil((((d as any) - (o as any)) / 86400000 + o.getDay() + 1) / 7)
      );
    })();
    let seen = null;
    try {
      seen = localStorage.getItem(LS_PEEK);
    } catch (e) {}
    if (seen !== wk) {
      setPeek(true);
      try {
        localStorage.setItem(LS_PEEK, wk);
      } catch (e) {}
      const t = setTimeout(() => setPeek(false), 4200);
      return () => clearTimeout(t);
    }
  }, [hasData]);

  if (!hasData) return null;

  // ── Map real task data → the design item shape ──
  const items = itemDTOs.map((d: any, i: number) => {
    const prog = d.taskItemProgressDTO;
    const amount = prog.amount;
    const threshold = d.threshold;
    const frac = Math.max(0, Math.min(1, threshold ? amount / threshold : 0));
    const isTime = d.sourceType === "ACTIVITY_TIME_SPENT";
    const st = sourceStyleOf(d.sourceType);
    // Generated tasks are now all learner-self-completable. The teacher-evaluation note only
    // applies to legacy in-flight BRANCH_ASSIGNMENT items (credited via a teacher's evaluate).
    const showNote = d.sourceType === "BRANCH_ASSIGNMENT";
    return {
      id: i,
      icon: st.icon,
      tint: st.tint,
      ink: st.ink,
      title: i18n.t(d.sourceType),
      readout: isTime
        ? `${(amount / 60 / 1000).toFixed(1)} / ${(
            threshold /
            60 /
            1000
          ).toFixed(1)} ${i18n.t("minute_abbrevation")}`
        : `${amount} / ${threshold}`,
      reward: d.rewardAmount,
      rewardType: d.rewardType,
      note: showNote ? i18n.t("after_teacher_evaluation") : null,
      frac,
      done: prog.completionStatus === "COMPLETED",
    };
  });

  const stateOf = (it: any) =>
    it.done ? "done" : it.frac > 0 ? "active" : "idle";
  const total = items.length;
  const completed = items.filter((it: any) => it.done).length;
  const pct = Math.round(
    (items.reduce((a: number, it: any) => a + it.frac, 0) / total) * 100,
  );
  // "Not started" must mean NO task has any progress — not merely that none are
  // fully COMPLETED. Otherwise a card with in-progress tasks (e.g. 2/4) but zero
  // finished ones wrongly reads "Başlanılmadı". Mirror the per-task `stateOf`
  // definition (frac > 0 ⇒ active) rather than the rounded `pct`.
  const started = completed > 0 || items.some((it: any) => it.frac > 0);
  const statusModifier = !started
    ? ""
    : completed === total
      ? "completed"
      : "inprogress";
  const statusText = !started
    ? i18n.t("NOT_STARTED")
    : completed === total
      ? i18n.t("COMPLETED")
      : i18n.t("ON_GOING");
  const totalXP = items.reduce((a: number, it: any) => a + (it.reward || 0), 0);
  const earnedXP = items.reduce(
    (a: number, it: any) => a + (it.done ? it.reward || 0 : 0),
    0,
  );
  const cheer =
    completed === total
      ? i18n.t("tasks_cheer_complete")
      : pct >= 50
        ? i18n.t("tasks_cheer_half")
        : completed > 0
          ? i18n.t("tasks_cheer_start")
          : i18n.t("tasks_cheer_none");
  const periodLabel = i18n.t(dto.type);
  const endShort = new Date(dto.endAt).toLocaleDateString(language, {
    day: "numeric",
    month: "short",
  });

  const fabStyle = pos
    ? { left: pos.x + "px", top: pos.y + "px", bottom: "auto" as const }
    : undefined;
  const modalStyle = (() => {
    const vw = window.innerWidth,
      vh = window.innerHeight;
    const p = pos || { x: 88, y: vh - 70 };
    const W = 376,
      M = 12,
      PILL = 66; // glass pill diameter
    // Horizontal: prefer to the right of the pill, flip to the left if it overflows.
    let left = p.x + 70 + M;
    if (left + W > vw - M) left = Math.max(M, p.x - W - M);
    // Vertical: anchor to the pill's nearest edge and cap the height to the
    // available space so the panel scrolls internally and never runs off-screen
    // (the content height varies with the task count, so a fixed guess overflows).
    const base: any = { left: left + "px", right: "auto" as const };
    if (p.y + PILL / 2 > vh / 2) {
      // pill in the lower half → open upward, pinned above the pill
      const bottom = Math.max(M, vh - p.y + M);
      base.top = "auto" as const;
      base.bottom = bottom + "px";
      base.maxHeight = Math.max(220, Math.min(740, vh - bottom - M)) + "px";
    } else {
      // pill in the upper half → open downward, pinned below the pill
      const top = Math.min(vh - M, p.y + PILL + M);
      base.bottom = "auto" as const;
      base.top = top + "px";
      base.maxHeight = Math.max(220, Math.min(740, vh - top - M)) + "px";
    }
    return base;
  })();

  return (
    <>
      <style>{taskProgressIndicatorStyles}</style>

      {/* backdrop */}
      {open ? (
        <div className="tk-backdrop" onClick={() => setOpen(false)} />
      ) : null}

      {/* anchored modal */}
      {open ? (
        <div
          className="tk-modal"
          role="dialog"
          aria-label={i18n.t("tasks")}
          style={modalStyle}
        >
          {/* gamified hero: title + progress ring + cheer + points + segment bar */}
          <div className="tk-hero">
            <button
              className="tk-close"
              aria-label={i18n.t("close")}
              onClick={() => setOpen(false)}
            >
              ✕
            </button>
            <div className="tk-hero__head">
              <span className="tk-modal__crest">🎯</span>
              <div className="tk-hero__headtxt">
                <div className="tk-modal__eyebrow">
                  {i18n.t("tasks_eyebrow_quest")}
                </div>
                <h3 className="tk-modal__title">{i18n.t("tasks_title")}</h3>
              </div>
              <span
                className={
                  "tk-statuspill" +
                  (statusModifier ? " tk-statuspill--" + statusModifier : "")
                }
              >
                {statusText}
              </span>
            </div>
            <div className="tk-hero__top">
              <div
                className="tk-ring"
                style={{
                  background: `conic-gradient(#F2865F ${
                    pct * 3.6
                  }deg, rgba(242,134,95,0.18) 0deg)`,
                }}
              >
                <div className="tk-ring__hole">
                  <span className="tk-ring__pct">{pct}%</span>
                  <span className="tk-ring__lbl">{i18n.t("progress")}</span>
                </div>
              </div>
              <div className="tk-hero__txt">
                <div className="tk-hero__cheer">{cheer}</div>
                <div className="tk-hero__count">
                  <b>{completed}</b> / {total} {i18n.t("tasks_done_word")}
                </div>
                <div className="tk-hero__xp">
                  <span className="tk-hero__xpico">
                    <Icon name="points" size={14} />
                  </span>
                  {earnedXP}{" "}
                  <span className="tk-hero__xpof">
                    / {totalXP} {i18n.t("pts_earned")}
                  </span>
                </div>
              </div>
            </div>
            {/* segmented quest bar — one segment per task */}
            <div className="tk-segbar">
              {items.map((it: any) => (
                <span
                  key={it.id}
                  className={"tk-seg tk-seg--" + stateOf(it)}
                  style={{ "--f": it.frac * 100 + "%" } as any}
                >
                  <span className="tk-seg__fill" />
                </span>
              ))}
            </div>
            <div className="tk-hero__meta">
              <span>🗓 {periodLabel}</span>
              <span className="tk-hero__dot">·</span>
              <span>
                {i18n.t("tasks_ends")} {endShort}
              </span>
            </div>
          </div>

          {/* task items — timeline cards, system-tracked */}
          <div className="tk-list">
            {items.map((it: any) => {
              const st = stateOf(it);
              return (
                <div key={it.id} className={"tk-row tk-row--" + st}>
                  <div className="tk-card" style={{ background: it.tint }}>
                    <div className="tk-card__main">
                      <span className="tk-card__tile" style={{ color: it.ink }}>
                        <TaskGlyph name={it.icon} />
                      </span>
                      <div className="tk-card__body">
                        <div className="tk-card__title">{it.title}</div>
                        <div className="tk-card__read">{it.readout}</div>
                        <div className="tk-card__sub">
                          {it.reward ? (
                            <span className="tk-reward">
                              ★ {it.reward} {i18n.t(it.rewardType)}
                            </span>
                          ) : (
                            <span className="tk-period">
                              {i18n.t("this_week")}
                            </span>
                          )}
                          {it.note ? (
                            <span className="tk-note">{it.note}</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="tk-card__status">
                      {st === "done" ? (
                        <span className="tk-pill tk-pill--done">
                          <TaskGlyph name="check" />
                        </span>
                      ) : st === "active" ? (
                        <span
                          className="tk-pill tk-pill--active"
                          style={{
                            background: `conic-gradient(#F2865F ${
                              it.frac * 360
                            }deg, rgba(242,134,95,0.2) 0deg)`,
                          }}
                        >
                          <span className="tk-pill__hole">
                            {Math.round(it.frac * 100)}%
                          </span>
                        </span>
                      ) : (
                        <span className="tk-pill tk-pill--idle" />
                      )}
                      <span className="tk-card__state">
                        {st === "done"
                          ? i18n.t("COMPLETED")
                          : st === "active"
                            ? i18n.t("ON_GOING")
                            : i18n.t("NOT_STARTED")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Smart task pill — draggable glass ring that peeks weekly, expands on click */}
      <div
        className={
          "tk-fabwrap" +
          (open ? " is-open" : "") +
          (dismissed ? " is-hidden" : "")
        }
        style={fabStyle}
      >
        <button
          className={
            "tk-pill-btn" +
            (open ? " is-open" : "") +
            (peek ? " is-peek" : "") +
            (dragging ? " is-dragging" : "")
          }
          aria-label={i18n.t("tasks")}
          aria-expanded={open}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
        >
          <span className="tk-glass" style={{ "--deg": pct * 3.6 + "deg" } as any}>
            <span className="tk-glass__ring" />
            <span className="tk-glass__disc">
              <span className="tk-glass__sheen" />
              <span className="tk-glass__pct">
                {completed}/{total}
              </span>
              <span className="tk-glass__lbl">{i18n.t("tasks_short_caps")}</span>
            </span>
          </span>
          <span className="tk-pill-label">
            <b>{i18n.t("tasks_weekly")}</b>
            <i>
              {completed}/{total} {i18n.t("done_word")} · {periodLabel}
            </i>
          </span>
        </button>
        <button
          className="tk-pill-dismiss"
          aria-label={i18n.t("tasks_hide")}
          title={i18n.t("tasks_hide")}
          onClick={minimize}
        >
          ✕
        </button>
      </div>

      {/* first-run tutorial */}
      {showTour ? <TaskTour pos={pos} onEnd={endTour} /> : null}

      {/* minimized → small reopen handle */}
      {dismissed ? (
        <button
          className="tk-reopen"
          aria-label={i18n.t("tasks_show")}
          title={i18n.t("tasks_weekly")}
          onClick={restore}
        >
          <span
            className="tk-reopen__ring"
            style={{ "--deg": pct * 3.6 + "deg" } as any}
          />
          <span className="tk-reopen__icon">
            <TaskGlyph name="sparkles" />
          </span>
          <span className="tk-reopen__txt">
            {completed}/{total}
          </span>
        </button>
      ) : null}
    </>
  );
};

export default TaskProgressIndicator;
